import { useRollbar } from "@rollbar/react";
import { useState, useEffect, useCallback } from "react";

import { queryClient } from "@api/queryClient";

import { useStoreContext } from "@context";

import {
  SendWebcamScreenshotParams,
  useSendWebcamConnectEvent,
} from "@shared/hooks/query/applicants/useApplicantWebcam";
import { useRouteParams } from "@shared/hooks/query/useRouteParams";
import { useWebcam } from "@shared/hooks/useWebcam";
import { dayjs } from "@shared/services/date";
import { LocalStorageConstants } from "@shared/services/enums";
import { localStorage } from "@shared/services/storage";

import { webcamQueryKey } from "../../../webcamScreenshotUploader/WebcamScreenshotUploader";
import { getDeviceLabelById } from "./WebcamEventUtils";

const SCREENSHOT_INTERVAL = 60; // seconds

export const WebcamEventHandler = () => {
  const { applicantExam, applicantExamId } = useStoreContext();

  const [webcamId, setWebcamId] = useState<string | undefined>(undefined);
  const [webcamLabel, setWebcamLabel] = useState<string | undefined>(undefined);
  const [isInitialized, setIsInitialized] = useState(false);

  const rollbar = useRollbar();

  const { challengeId } = useRouteParams();
  const challengeResultId =
    applicantExam?.getResultByChallengeId(challengeId).id;

  const { mutate: sendWebcamConnectEvent } = useSendWebcamConnectEvent({
    challengeResultId: challengeResultId ?? 0,
  });

  const onConnect = useCallback(() => {
    if (isInitialized) {
      sendWebcamConnectEvent({
        eventType: "webcam-started",
        mediaDeviceId: webcamId, // Optional
        mediaDeviceLabel: webcamLabel, // Optional
      });
    }
  }, [sendWebcamConnectEvent, webcamId, webcamLabel, isInitialized]);
  const onDisconnect = useCallback(() => {
    if (isInitialized) {
      sendWebcamConnectEvent({
        eventType: "webcam-stopped",
        mediaDeviceId: webcamId, // Optional
        mediaDeviceLabel: webcamLabel, // Optional
      });
      setIsInitialized(false);
    }
  }, [sendWebcamConnectEvent, webcamId, webcamLabel, isInitialized]);
  const onUnavailable = useCallback(() => {
    sendWebcamConnectEvent({
      eventType: "webcam-unavailable",
    });
  }, [sendWebcamConnectEvent]);

  const [defaultWebcamId] = useState(() => {
    const localStorageWebcamId: string | undefined = localStorage.getItem(
      LocalStorageConstants.Webcam,
    );

    return localStorageWebcamId;
  });

  const {
    isCapturing,
    videoRef,
    canvasRef,
    getWebcamScreenshot,
    selectedVideoSettings,
  } = useWebcam({
    onConnect,
    onDisconnect,
    onUnavailable,
    defaultWebcamId,
  });

  useEffect(() => {
    if (selectedVideoSettings) {
      // This will give us the device id, which is consistent across page reloads, but not the label.
      const deviceId = selectedVideoSettings.deviceId;

      if (deviceId) {
        setWebcamId(deviceId);
        getDeviceLabelById(deviceId).then((response) => {
          if (response && response.label) {
            setWebcamLabel(response.label);
            setIsInitialized(true);
          } else {
            rollbar.error(response?.error || "Unable to get webcam label");
          }
        });
      }
    }
  }, [selectedVideoSettings]);

  useEffect(() => {
    if (!isCapturing) {
      return;
    }

    const interval = setInterval(() => {
      const screenshot = getWebcamScreenshot();

      if (screenshot) {
        const newData: SendWebcamScreenshotParams = {
          applicantExamId,
          challengeResultId: challengeResultId ?? 0,
          imgDataUrl: screenshot,
          timestamp: dayjs().format(),
        };

        queryClient.setQueryData<SendWebcamScreenshotParams>(
          webcamQueryKey(applicantExamId),
          newData,
        );
      } else {
        rollbar.error("Unable to get screenshot");
        onUnavailable();
      }
    }, SCREENSHOT_INTERVAL * 1000);

    return () => {
      clearInterval(interval);
    };
  }, [
    getWebcamScreenshot,
    rollbar,
    applicantExamId,
    challengeResultId,
    isCapturing,
    onUnavailable,
  ]);

  return (
    <div className="code-webcam-event-handler">
      <video ref={videoRef} muted />
      <canvas ref={canvasRef} />
    </div>
  );
};
